import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';

/**
 * Gecachte Routen-Pfade die im Speicher gehalten werden.
 *
 * Nur Listen-Seiten werden gecacht, damit beim Zurueck-Navigieren
 * Scroll-Position und Filter-Zustand erhalten bleiben.
 */
const GECACHTE_ROUTEN: ReadonlySet<string> = new Set([
  'projekte',
  'meine-aufgaben',
  'aufgaben',
  'lieferanten',
]);

/** Maximale Anzahl gecachter Routen (FIFO – aelteste wird entfernt). */
const MAX_CACHE_GROESSE = 5;

/**
 * Custom RouteReuseStrategy die Listen-Seiten im Speicher haelt.
 *
 * Wenn ein User von einer Liste (z.B. Aufgaben) zu einem Detail navigiert
 * und dann zurueck kommt, bleibt der Scroll-Stand und Filter-Zustand erhalten.
 *
 * Nur explizit konfigurierte Routen werden gecacht – nicht alle.
 * Maximale Cache-Groesse: 5 Routen (aelteste wird entfernt).
 */
export class AppRouteReuseStrategy implements RouteReuseStrategy {

  /** Interner Cache: Pfad → gespeichertes Handle. */
  private _cache = new Map<string, DetachedRouteHandle>();

  /** Reihenfolge der Eintraege fuer FIFO-Eviction. */
  private _reihenfolge: string[] = [];

  /**
   * Bestimmt ob die Route beim Verlassen im Speicher gehalten werden soll.
   *
   * @returns `true` wenn die Route in {@link GECACHTE_ROUTEN} enthalten ist.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const pfad = this._pfadExtrahieren(route);
    return pfad !== null && GECACHTE_ROUTEN.has(pfad);
  }

  /**
   * Speichert eine abgehaengte Route im Cache.
   *
   * Entfernt den aeltesten Eintrag wenn die maximale Cache-Groesse ueberschritten wird.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const pfad = this._pfadExtrahieren(route);
    if (pfad === null) return;

    if (handle === null) {
      // Angular signalisiert: Route soll aus dem Cache entfernt werden
      this._cache.delete(pfad);
      this._reihenfolge = this._reihenfolge.filter(p => p !== pfad);
      return;
    }

    // Falls der Pfad schon existiert, Reihenfolge aktualisieren
    if (this._cache.has(pfad)) {
      this._reihenfolge = this._reihenfolge.filter(p => p !== pfad);
    }

    this._cache.set(pfad, handle);
    this._reihenfolge.push(pfad);

    // FIFO-Eviction: aeltesten Eintrag entfernen wenn Cache voll
    if (this._reihenfolge.length > MAX_CACHE_GROESSE) {
      const aeltester = this._reihenfolge.shift()!;
      this._cache.delete(aeltester);
    }
  }

  /**
   * Prueft ob eine gecachte Version der Route vorhanden ist.
   *
   * @returns `true` wenn die Route im Cache liegt und wiederverwendet werden kann.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const pfad = this._pfadExtrahieren(route);
    return pfad !== null && this._cache.has(pfad);
  }

  /**
   * Gibt das gecachte Route-Handle zurueck.
   *
   * @returns Das gespeicherte Handle oder `null` wenn keins vorhanden.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const pfad = this._pfadExtrahieren(route);
    if (pfad === null) return null;
    return this._cache.get(pfad) ?? null;
  }

  /**
   * Standard-Angular-Verhalten: Gleiche Route-Config → Route wiederverwenden.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Leert den kompletten Cache.
   *
   * Sollte z.B. nach einem Logout aufgerufen werden,
   * damit keine Daten des vorherigen Users im Speicher bleiben.
   */
  leeren(): void {
    this._cache.clear();
    this._reihenfolge = [];
  }

  /** Gibt die aktuelle Anzahl gecachter Routen zurueck. */
  get cacheGroesse(): number {
    return this._cache.size;
  }

  /**
   * Extrahiert den Routen-Pfad aus einem ActivatedRouteSnapshot.
   *
   * Baut den vollstaendigen Pfad aus den routeConfig-Segmenten zusammen.
   */
  private _pfadExtrahieren(route: ActivatedRouteSnapshot): string | null {
    if (route.routeConfig?.path) {
      return route.routeConfig.path;
    }
    return null;
  }
}
